import { Link } from 'react-router-dom';

import { useRoutesProvider } from '../../../providers/provider-hooks';
import CadruPasVideo from '../components/CadruPasVideo';
import VideoIntroPage from './VideoIntroPage';
import { useOnboardingVideo } from '../hooks/useOnboardingVideo';

/**
 * Pasul 3 din inscriere, montat pe ruta — invelisul care aduce SURSA.
 *
 * Pana la b21, ruta monta direct `VideoIntroPage`, care avea URL-ul scris in cod
 * ca valoare implicita de prop: filmuletul de test dat de owner in august. Acum
 * adresa vine din configurarea backendului
 * (`GET /api/editions/current/onboarding-video`), deci ecranul are un moment in
 * care inca nu stie ce sa arate, si un caz in care nu va sti niciodata.
 *
 * 🔴 NICIODATA filmuletul de test ca rezerva. Un fallback ar arata pe ecran exact
 * ca un succes — acelasi player, acelasi buton, aceeasi poarta care se deschide —
 * dar cu ALT material decat cel al clientului. Regula sta in `utils/video-sursa.ts`,
 * pura si testata, nu in randarea de aici; componenta asta nu are de unde sa
 * inventeze un URL nici daca ar vrea.
 *
 * ⚠️ Cele doua cauze de `indisponibil` — „acum nu merge" (retea, 401/403, 5xx) si
 * „nu e configurat niciun film" (200 cu `litiuUrl` gol) — se colapseaza intr-un
 * SINGUR ecran, cu acordul lui S1: profesorul n-are ce face cu diferenta, si
 * niciuna din ele nu se repara de la el.
 */
export default function VideoInscrierePage() {
    const sursa = useOnboardingVideo();
    const { PATHS, PAGES } = useRoutesProvider();
    const caleAsteptare: string = PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER];

    /* Cadrul se randeaza de la prima pictura, cu playerul inlocuit de un
       dreptunghi gol de aceeasi forma (`aspect-video`): cerinta lui S1 e ca
       titlul si caseta sa nu se mute cu niciun pixel cand soseste raspunsul.
       Fara spinner — un cadru care isi tine locul spune deja ca se incarca, iar
       un al doilea indicator ar concura cu pastila. */
    if (sursa.stare === 'se-incarca') {
        return (
            <CadruPasVideo pastila="se încarcă…">
                <div className="overflow-hidden rounded-lg border border-hairline bg-white">
                    <div className="relative aspect-video w-full bg-light" />
                </div>
            </CadruPasVideo>
        );
    }

    if (sursa.stare === 'indisponibil') {
        return (
            /* Pastila lipseste deliberat (`pastila={null}`): pe un ecran care spune
               ca filmuletul nu e disponibil, nimic nu are voie sa ramana promitand
               o durata care nu mai vine. Titlul si paragraful descriptiv raman —
               ele descriu, nu promit, si fara ele caseta ar pluti fara subiect. */
            <CadruPasVideo pastila={null}>
                {/* Aceeasi caseta ca in `VideoIntroPage` (`data-eroare-video`,
                    `role="alert"`), cu aceeasi iesire proprie: fara ea ecranul e un
                    fund de sac — poarta ramane inchisa si omul n-are unde sa se duca.

                    🔴 Textul NU e cel din `VideoIntroPage` («Poți încerca mai
                    târziu»), la cererea lui S1 si pe bun temei: cand cauza e o
                    setare care lipseste la noi, nicio reincercare a profesorului
                    n-o rezolva, iar textul i-ar da o sarcina inutila. Si nu e nici
                    «Materialul video nu a putut fi identificat.» — ala descrie un
                    URL stricat si l-ar trimite sa caute o problema care e a noastra.

                    Fraza a doua e VERIFICATA, nu o formula linistitoare: S1 a
                    masurat pe backend ca `videoVazutDt` se scrie doar prin
                    `PATCH /own/video` (`SchoolRequestService:295-303`) si ca niciun
                    drum de aprobare nu-l citeste ca poarta. */}
                <div
                    data-eroare-video
                    role="alert"
                    className="rounded-lg border border-error bg-error/10 p-3 text-small text-error"
                >
                    <p className="m-0">
                        <strong>Filmulețul nu este disponibil acum.</strong> Pasul acesta nu
                        blochează cererea ta de înscriere — poți merge mai departe.
                    </p>
                    <Link
                        to={caleAsteptare}
                        className="mt-2.5 flex min-h-11 w-full items-center justify-center rounded-md border border-input bg-white px-4 font-semibold text-dark"
                    >
                        Înapoi la pagina de așteptare
                    </Link>
                </div>
            </CadruPasVideo>
        );
    }

    return <VideoIntroPage urlVideo={sursa.url} />;
}
